import styled from '@emotion/styled';
import Popover from '@mui/material/Popover';
import { usePopupState } from 'material-ui-popup-state/hooks';
import { ProjectType } from '@/enums';
import ProgressBar from '../presentation/ProgressBar';
import type {
  ProjectProgressProps,
  ProjectProgressPopoverProps,
} from './interfaces';
import styles from './style/ProjectProgress.module.css';

export function ProjectProgressBar(props: ProjectProgressProps) {
  const popupState = usePopupState({
    popupId: 'project-progress-popover',
    variant: 'popover',
  });

  return (
    <>
      <ProgressBar
        className={props.className}
        onMouseEnter={popupState.open}
        onMouseLeave={popupState.close}
        pct={props.metrics.progress.percentage} />
      <Popover
        sx={{
          pointerEvents: 'none',
          marginTop: '10px',
        }}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: 'var(--pri-01)',
              border: '1px solid transparent',
              borderRadius: '4px',
              boxShadow: '0 2px 6px 2px rgba(60,64,67,.15)',
            },
          },
        }}
        disableScrollLock
        open={popupState.isOpen}
        anchorEl={popupState.anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        onClose={popupState.close}
        disableRestoreFocus>
        <ProjectProgressPopover
          includeRejected={props.includeRejected}
          metrics={props.metrics}
          project={props.project} />
      </Popover>
    </>
  );
}

export function ProjectProgressPopover({
  includeRejected = false,
  metrics,
  project,
}: ProjectProgressPopoverProps) {
  // const isInternalUser = useIsInternalUser();

  const callType = project.projectTypeId === ProjectType.Call;
  const surveyType = project.projectTypeId === ProjectType.Survey;

  const goal = surveyType
    ? 'responses'
    : 'calls';

  const rejected = metrics.members.rejectedCount
    + metrics.members.disqualifiedCount;

  const scheduling = metrics.calls.scheduledCount
    + metrics.calls.pendingCount
    + metrics.calls.reschedulingCount;

  const completedCalls = `${metrics.calls.completedCount} calls`;
  const completedSurveys = `${metrics.surveys.activeCount} responses`;

  const completed = surveyType
    ? completedSurveys
    : callType
      ? completedCalls
      : `${completedCalls}, ${completedSurveys}`;

  // const pipeline = metrics.members.recommendedCount
  //   + metrics.members.addedCount
  //   + metrics.members.pendingCount
  //   + metrics.members.onboardedCount
  //   + metrics.members.joinedCount;

  return (
    <div className={styles.root}>
      <div className={styles.goal}>Goal: {metrics.progress.goal} {goal}</div>
      {includeRejected && (
        <Rejected>
          Rejected / Disqualified - {rejected} experts
        </Rejected>
      )}
      {/* {isInternalUser && (
        <Pipeline>
          Pipeline - {pipeline} experts
        </Pipeline>
      )} */}
      {!surveyType && (
        <Scheduling>
          Scheduling - {scheduling} calls
        </Scheduling>
      )}
      <Completed>
        Completed - {completed}
      </Completed>
    </div>
  );
}

const Rejected = styled.div`
  display: flex;
  white-space: nowrap;
  margin-bottom: 10px;
  color: var(--black-l);

  &:before {
    display: inline-block;
    content: '';
    height: 15px;
    width: 15px;
    margin-right: 5px;
    border-radius: 50%;
    background-color: ${props => props.theme.palette.primary.light1};
  }
`;

// const Pipeline = styled.div`
//   display: flex;
//   white-space: nowrap;
//   margin-bottom: 10px;
//   color: var(--black-l);

//   &:before {
//     display: inline-block;
//     content: '';
//     height: 15px;
//     width: 15px;
//     margin-right: 5px;
//     border-radius: 50%;
//     background-color: ${props => props.theme.palette.primary.light5};
//   }
// `;

const Scheduling = styled.div`
  display: flex;
  white-space: nowrap;
  margin-bottom: 10px;
  color: var(--black-l);

  &:before {
    display: inline-block;
    content: '';
    height: 15px;
    width: 15px;
    margin-right: 5px;
    border-radius: 50%;
    background-color: ${props => props.theme.palette.primary.light3};
  }
`;

const Completed = styled.div`
  display: flex;
  white-space: nowrap;
  margin-bottom: 10px;
  color: var(--black-l);

  &:before {
    display: inline-block;
    content: '';
    height: 15px;
    width: 15px;
    margin-right: 5px;
    border-radius: 50%;
    background-color: ${props => props.theme.palette.primary.main};
  }
`;